import { Fragment, useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Info, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

const DRILL_PAGE_SIZE = 100;

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function fmtNum(n) {
  if (typeof n !== "number") return "—";
  return n.toLocaleString();
}

function fmtUsd(n) {
  if (typeof n !== "number") return "—";
  if (n === 0) return "$0";
  // Below $0.01 — show micro-cents with extra precision so very small
  // calls don't collapse to $0.00 and read as untracked.
  if (n < 0.01) return `$${n.toFixed(5)}`;
  if (n < 1) return `$${n.toFixed(4)}`;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

// Hover-only breakdown popover. CSS-driven via Tailwind `group-hover:`
// so there's no React state per row. `placement="top"` is used inside
// table rows (above the icon); `placement="bottom"` for StatCards at
// the page header where there's no room above.
function BreakdownTooltip({ breakdown, total, placement = "top", anchor = "right", iconSize = 11 }) {
  const b = breakdown || {};
  const rows = [
    ["Input", b.input],
    ["Output", b.output],
    ["Cache read", b.cache_read],
    ["Cache write", b.cache_creation],
  ];
  const popCls = [
    "pointer-events-none absolute z-50 hidden group-hover:block",
    placement === "top" ? "bottom-full mb-1" : "top-full mt-1",
    anchor === "right" ? "right-0" : "left-0",
    "bg-black text-white text-[11px] rounded-md px-3 py-2 shadow-lg min-w-[200px]",
  ].join(" ");
  return (
    <span className="relative group inline-flex items-center">
      <Info size={iconSize} className="text-neutral-400 group-hover:text-neutral-700 transition-colors" />
      <div className={popCls}>
        <div className="font-semibold mb-1.5 text-neutral-300 uppercase tracking-[0.1em] text-[9.5px]">
          Cost breakdown
        </div>
        {rows.map(([label, val]) => (
          <div key={label} className="flex items-center justify-between gap-3 py-0.5">
            <span className="text-neutral-300">{label}</span>
            <span className="tabular font-medium">{fmtUsd(val ?? 0)}</span>
          </div>
        ))}
        <div className="border-t border-white/20 mt-1.5 pt-1.5 flex items-center justify-between gap-3">
          <span className="text-neutral-300 font-semibold">Total</span>
          <span className="tabular font-bold">{fmtUsd(total)}</span>
        </div>
      </div>
    </span>
  );
}

// Inline cost cell with the breakdown tooltip beside it.
function CostCell({ cost, breakdown }) {
  return (
    <span className="inline-flex items-center gap-1.5 justify-end">
      <span className="tabular font-semibold">{fmtUsd(cost)}</span>
      <BreakdownTooltip breakdown={breakdown} total={cost} placement="top" anchor="right" />
    </span>
  );
}

function shortId(id, n = 10) {
  if (!id) return "—";
  return id.length > n + 2 ? `${id.slice(0, n)}…` : id;
}

function StatCard({ label, value, sub, trailing = null }) {
  return (
    <div className="rounded-[14px] border border-black/5 bg-white p-5 overflow-visible">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10.5px] uppercase tracking-[0.12em] opacity-60">{label}</div>
        {trailing}
      </div>
      <div className="font-display tabular text-3xl font-extrabold mt-2.5 leading-none">{value}</div>
      {sub && <div className="text-[11px] opacity-60 mt-1.5">{sub}</div>}
    </div>
  );
}

function Pagination({ page, totalPages, total, onChange, loading }) {
  if (!total) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-black/5 text-[11.5px] text-neutral-600 bg-neutral-50">
      <div>
        Showing <span className="tabular font-medium">{(page - 1) * DRILL_PAGE_SIZE + 1}</span>
        –<span className="tabular font-medium">{Math.min(page * DRILL_PAGE_SIZE, total)}</span>
        {" "}of <span className="tabular font-medium">{total.toLocaleString()}</span>
        {loading && <span className="ml-2 text-neutral-400">refreshing…</span>}
      </div>
      <div className="flex items-center gap-1.5">
        <button type="button" onClick={() => onChange(1)} disabled={page <= 1 || loading} className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40">« First</button>
        <button type="button" onClick={() => onChange(Math.max(1, page - 1))} disabled={page <= 1 || loading} className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40">‹ Prev</button>
        <span className="px-2 tabular">Page {page} / {Math.max(1, totalPages)}</span>
        <button type="button" onClick={() => onChange(Math.min(totalPages || 1, page + 1))} disabled={page >= totalPages || loading} className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40">Next ›</button>
        <button type="button" onClick={() => onChange(totalPages || 1)} disabled={page >= totalPages || loading} className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40">Last »</button>
      </div>
    </div>
  );
}

function UserDrillDown({ userId }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchPage = useCallback(async (p) => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/admin/llm-usage/users/${userId}`, {
        params: { page: p, page_size: DRILL_PAGE_SIZE },
      });
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchPage(page); }, [page, fetchPage]);

  if (err) {
    return <div className="px-4 py-4 text-[12.5px] text-red-600">{err}</div>;
  }
  if (!data) {
    return <div className="px-4 py-4 text-[12.5px] text-neutral-500">Loading…</div>;
  }
  const items = data.items || [];
  if (items.length === 0) {
    return <div className="px-4 py-4 text-[12.5px] text-neutral-500">No calls.</div>;
  }
  return (
    <div className="bg-neutral-50/60 overflow-visible">
      <table className="w-full text-[12px]">
        <thead className="text-left text-neutral-500">
          <tr>
            <th className="py-1.5 px-3 font-medium">Time</th>
            <th className="py-1.5 px-3 font-medium">Thread</th>
            <th className="py-1.5 px-3 font-medium">Round</th>
            <th className="py-1.5 px-3 font-medium">Model</th>
            <th className="py-1.5 px-3 font-medium text-right">Input</th>
            <th className="py-1.5 px-3 font-medium text-right">Output</th>
            <th className="py-1.5 px-3 font-medium text-right">Cache R</th>
            <th className="py-1.5 px-3 font-medium text-right">Cache W</th>
            <th className="py-1.5 px-3 font-medium text-right">Cost</th>
            <th className="py-1.5 px-3 font-medium">Stop</th>
            <th className="py-1.5 px-3 font-medium">Prompt</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr
              key={r.id}
              className="border-t border-black/5 hover:bg-white"
              data-testid={`llm-usage-row-${r.id}`}
            >
              <td className="py-1.5 px-3 whitespace-nowrap text-neutral-700">{formatDateTime(r.created_at)}</td>
              <td className="py-1.5 px-3 font-mono text-[11px] whitespace-nowrap">
                {r.thread_deleted ? (
                  <span className="text-neutral-400" title={r.thread_id}>
                    {shortId(r.thread_id)} <span className="italic">(deleted)</span>
                  </span>
                ) : (
                  <span className="text-neutral-700" title={r.thread_id}>{shortId(r.thread_id)}</span>
                )}
              </td>
              <td className="py-1.5 px-3 tabular text-neutral-700">{r.round_idx}</td>
              <td className="py-1.5 px-3 font-mono text-[11px] text-neutral-600 whitespace-nowrap">{r.model}</td>
              <td className="py-1.5 px-3 tabular text-right">{fmtNum(r.input_tokens)}</td>
              <td className="py-1.5 px-3 tabular text-right">{fmtNum(r.output_tokens)}</td>
              <td className="py-1.5 px-3 tabular text-right text-neutral-500">{fmtNum(r.cache_read_input_tokens)}</td>
              <td className="py-1.5 px-3 tabular text-right text-neutral-500">{fmtNum(r.cache_creation_input_tokens)}</td>
              <td className="py-1.5 px-3 text-right">
                <CostCell cost={r.cost_usd} breakdown={r.cost_breakdown_usd} />
              </td>
              <td className="py-1.5 px-3 text-[11px] text-neutral-500">{r.stop_reason || "—"}</td>
              <td className="py-1.5 px-3 text-[11.5px] text-neutral-600 max-w-[420px] truncate" title={r.user_prompt}>
                {r.user_prompt || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        page={data.page}
        totalPages={data.total_pages}
        total={data.total}
        loading={loading}
        onChange={setPage}
      />
    </div>
  );
}

export default function AdminLLMUsage() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null); // user_id or null

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/admin/llm-usage/summary");
      setSummary(data);
    } catch (e) {
      setErr(
        e?.response?.status === 403
          ? "Admin only."
          : e?.response?.data?.detail || e.message || "Failed to load",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const totals = summary?.totals || {};
  const rows = summary?.rows || [];

  return (
    <AdminLayout
      activeTab="llm-usage"
      title="LLM usage"
      right={
        <button onClick={refresh} disabled={loading} className="btn btn-ghost text-[12.5px]">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      }
    >
      <div data-testid="admin-llm-usage-page">
        <div className="section-eyebrow">Admin</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">LLM token usage</h1>
        <p className="text-neutral-500 text-[13px] mt-1.5 max-w-2xl leading-relaxed">
          Per-user totals for Plan-a-Trip chat. One row per user. Click any
          row to drill into individual API calls (newest first).
          Rows survive thread deletion — see "(deleted)" tag below.
        </p>

        {err && (
          <div className="mt-4 p-3 border rounded-[10px] text-[13px]" style={{ background: "var(--bad-bg)", color: "var(--bad)", borderColor: "var(--bad-border)" }}>
            {err}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <StatCard label="Users with usage" value={fmtNum(totals.users || 0)} />
          <StatCard label="Total calls" value={fmtNum(totals.calls || 0)} />
          <StatCard
            label="Total tokens"
            value={fmtNum(totals.total_tokens || 0)}
            sub={`${fmtNum(totals.input_tokens || 0)} in · ${fmtNum(totals.output_tokens || 0)} out`}
          />
          <StatCard
            label="Total cost"
            value={fmtUsd(totals.cost_usd || 0)}
            sub={`${fmtNum((totals.cache_read_input_tokens || 0) + (totals.cache_creation_input_tokens || 0))} cache tokens (${fmtNum(totals.cache_read_input_tokens || 0)} read · ${fmtNum(totals.cache_creation_input_tokens || 0)} write)`}
            trailing={
              <BreakdownTooltip
                breakdown={totals.cost_breakdown_usd}
                total={totals.cost_usd || 0}
                placement="bottom"
                anchor="right"
                iconSize={13}
              />
            }
          />
        </div>

        <div className="card overflow-visible mt-7" data-testid="admin-llm-usage-table">
          <table className="w-full text-[13px]">
            <thead className="text-left text-neutral-500 bg-neutral-50">
              <tr>
                <th className="py-2 px-3 font-medium w-6"></th>
                <th className="py-2 px-3 font-medium">User</th>
                <th className="py-2 px-3 font-medium text-right">Calls</th>
                <th className="py-2 px-3 font-medium text-right">Input</th>
                <th className="py-2 px-3 font-medium text-right">Output</th>
                <th className="py-2 px-3 font-medium text-right">Cache R</th>
                <th className="py-2 px-3 font-medium text-right">Cache W</th>
                <th className="py-2 px-3 font-medium text-right">Total</th>
                <th className="py-2 px-3 font-medium text-right">Cost</th>
                <th className="py-2 px-3 font-medium">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={10} className="py-8 text-center text-neutral-500 text-[13px]">
                    No LLM usage logged yet. Token rows start populating the moment any user sends a Plan-a-Trip message or anyone runs the public landing-page demo.
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const isOpen = expanded === r.user_id;
                return (
                  <Fragment key={r.user_id}>
                    <tr
                      className="border-t border-black/5 hover:bg-neutral-50/60 cursor-pointer"
                      onClick={() => setExpanded(isOpen ? null : r.user_id)}
                      data-testid={`llm-usage-user-${r.user_id}`}
                    >
                      <td className="py-2 px-3 text-neutral-400">
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </td>
                      <td className="py-2 px-3">
                        <div className="font-medium">{r.email || <span className="italic text-neutral-400">{`<deleted user>`}</span>}</div>
                        {r.name && <div className="text-[11.5px] text-neutral-500">{r.name}</div>}
                      </td>
                      <td className="py-2 px-3 tabular text-right">{fmtNum(r.calls)}</td>
                      <td className="py-2 px-3 tabular text-right">{fmtNum(r.input_tokens)}</td>
                      <td className="py-2 px-3 tabular text-right">{fmtNum(r.output_tokens)}</td>
                      <td className="py-2 px-3 tabular text-right text-neutral-500">{fmtNum(r.cache_read_input_tokens)}</td>
                      <td className="py-2 px-3 tabular text-right text-neutral-500">{fmtNum(r.cache_creation_input_tokens)}</td>
                      <td className="py-2 px-3 tabular text-right font-semibold">{fmtNum(r.total_tokens)}</td>
                      <td className="py-2 px-3 text-right">
                        <CostCell cost={r.cost_usd} breakdown={r.cost_breakdown_usd} />
                      </td>
                      <td className="py-2 px-3 text-neutral-600 text-[12px] whitespace-nowrap">{formatDateTime(r.last_at)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-black/5">
                        <td colSpan={10} className="p-0">
                          <UserDrillDown userId={r.user_id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
